import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  listEmployeeManagers, saveEmployeeManager, removeEmployeeManager,
} from "../api";
import { Card, Field, Select, Loading, ErrorState, EmptyState } from "./ui";
import { fmtDate } from "../lib/format";

/** Labour is monthly, so a change always lands on the 1st of its month. */
const firstOfMonth = (iso) => (iso ? `${String(iso).slice(0, 7)}-01` : "");

const SORTS = [
  { key: "employee", label: "Employee" },
  { key: "manager",  label: "Manager" },
  { key: "from",     label: "Effective from" },
  { key: "changed",  label: "Last edited" },
];

export default function EmployeeManagers() {
  const [data, setData] = useState({ assignments: [], employees: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [sort, setSort] = useState({ key: "employee", dir: 1 });
  const [onlyChanged, setOnlyChanged] = useState(false);

  // new-assignment form
  const [empCode, setEmpCode] = useState("");
  const [manager, setManager] = useState("");
  const [from, setFrom] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    listEmployeeManagers()
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const nameOf = useMemo(() => {
    const m = {};
    data.employees.forEach((e) => { m[e.employee_code] = e.employee_name; });
    return m;
  }, [data.employees]);

  /** How many dated rows each employee has - 1 means never reassigned. */
  const counts = useMemo(() => {
    const c = {};
    data.assignments.forEach((a) => { c[a.employee_code] = (c[a.employee_code] || 0) + 1; });
    return c;
  }, [data.assignments]);

  const rows = useMemo(() => {
    const list = data.assignments
      .map((a) => ({
        ...a,
        employee: a.employee_name || nameOf[a.employee_code] || a.employee_code,
        changes: counts[a.employee_code] || 1,
      }))
      .filter((a) => !onlyChanged || a.changes > 1);

    const pick = {
      employee: (r) => `${r.employee} ${r.effective_from}`,
      manager:  (r) => `${r.manager} ${r.employee}`,
      from:     (r) => `${r.effective_from} ${r.employee}`,
      changed:  (r) => `${r.updated_at || ""} ${r.employee}`,
    }[sort.key];

    return list.sort((a, b) => {
      const x = pick(a), y = pick(b);
      return x < y ? -sort.dir : x > y ? sort.dir : 0;
    });
  }, [data.assignments, nameOf, counts, sort, onlyChanged]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await saveEmployeeManager(empCode, manager, firstOfMonth(from), note);
      setMsg(
        `${nameOf[empCode] || empCode} moves to ${manager.toUpperCase()} from ${fmtDate(firstOfMonth(from))}`
      );
      setNote("");
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (row.changes <= 1) {
      setError(
        `${row.employee} has only this one assignment - reassign rather than delete, or the dashboard has nobody to attribute their hours to.`
      );
      return;
    }
    setError("");
    try {
      await removeEmployeeManager(row.id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <Loading label="Loading manager history..." />;
  if (error && !data.assignments.length) return <ErrorState message={error} onRetry={load} />;

  const arrow = (key) => (sort.key === key ? (sort.dir > 0 ? " ▲" : " ▼") : "");

  return (
    <Card
      title="Employee managers"
      note="who owns an employee, and from when - the dashboard reads the latest row dated on or before each month"
      flush
    >
      <form className="dept-add" onSubmit={submit}>
        <Field label="Employee">
          <Select
            value={empCode}
            onChange={setEmpCode}
            options={[
              { value: "", label: "Pick an employee..." },
              ...data.employees.map((e) => ({
                value: e.employee_code, label: e.employee_name || e.employee_code,
              })),
            ]}
            style={{ minWidth: 200 }}
          />
        </Field>
        <Field label="Moves to">
          <Select
            value={manager}
            onChange={setManager}
            options={[
              { value: "", label: "Pick a manager..." },
              ...data.users.map((u) => ({ value: u, label: u.toUpperCase() })),
            ]}
            style={{ minWidth: 130 }}
          />
        </Field>
        <Field label="From">
          <input
            className="input"
            type="month"
            value={String(from).slice(0, 7)}
            onChange={(e) => setFrom(`${e.target.value}-01`)}
          />
        </Field>
        <Field label="Note">
          <input
            className="input"
            type="text"
            placeholder="promoted, team split..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ minWidth: 170 }}
          />
        </Field>
        <button
          className="btn btn--primary"
          type="submit"
          disabled={busy || !empCode || !manager || !from}
        >
          {busy ? "Saving..." : "Add change"}
        </button>
        <button
          type="button"
          className={`btn${onlyChanged ? " is-on" : ""}`}
          onClick={() => setOnlyChanged((v) => !v)}
        >
          {onlyChanged ? "Showing moves only" : "Show moves only"}
        </button>
      </form>

      {error && <div className="notice notice--bad">{error}</div>}
      {msg && <div className="notice">{msg}</div>}

      <p className="toolbar-note dept-hint">
        Every employee starts with one row, dated to their first timesheet month and
        holding the manager they are on now. Add a dated row each time someone moves -
        earlier rows stay, so the history is preserved and past months keep reporting
        under whoever owned them at the time. A change always takes effect from the
        first of its month.
      </p>

      {!rows.length ? (
        <EmptyState
          title="Nothing to show"
          hint={onlyChanged ? "No employee has been reassigned yet." : "No assignments recorded."}
        />
      ) : (
        <div className="tbl-wrap tbl-wrap--flush">
          <table className="tbl tbl-zebra">
            <thead>
              <tr>
                {SORTS.map((s) => (
                  <th
                    key={s.key}
                    className={s.key === "employee" ? "" : "num"}
                    onClick={() => toggleSort(s.key)}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {s.label}{arrow(s.key)}
                  </th>
                ))}
                <th>Note</th>
                <th style={{ width: 70 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.employee}
                    {r.changes > 1 && (
                      <span className="badge" style={{ marginLeft: 8 }}>
                        {r.changes} rows
                      </span>
                    )}
                  </td>
                  <td className="num"><span className="badge">{r.manager.toUpperCase()}</span></td>
                  <td className="num">{fmtDate(r.effective_from)}</td>
                  <td className="num">{r.updated_at ? fmtDate(r.updated_at) : "—"}</td>
                  <td className="truncate" title={r.note || ""}>
                    {r.note || <span className="dash">{"—"}</span>}
                  </td>
                  <td>
                    <button
                      className="btn btn--danger btn--sm"
                      onClick={() => remove(r)}
                      disabled={r.changes <= 1}
                      title={r.changes <= 1
                        ? "The only assignment for this employee"
                        : "Remove this change"}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
